import type { BridgeChartDrawingAnchors, BridgeChartDrawingMigrationContext, BridgeChartDrawingPoint, BridgeChartMarketPoint } from "../types/public";

const DAY = 86_400_000;
export interface DrawingTimeAxis { readonly times: readonly number[]; readonly singleBarStep: number }
export interface DrawingTransform {
  capture(point: BridgeChartDrawingPoint): BridgeChartMarketPoint;
  project(point: BridgeChartMarketPoint): BridgeChartDrawingPoint;
  anchors(start: BridgeChartDrawingPoint, end: BridgeChartDrawingPoint): BridgeChartDrawingAnchors;
  matches(anchors: BridgeChartDrawingAnchors): boolean;
}

/** Validate/copy once per data assignment, never on each viewport redraw. */
export function createDrawingTimeAxis(dates: readonly string[], timeframe = "1D"): DrawingTimeAxis {
  const times = dates.map(date => Date.parse(date));
  if (times.some((time, i) => !Number.isFinite(time) || (i > 0 && time <= times[i - 1]))) {
    throw new TypeError("Drawing dates must be valid and strictly increasing.");
  }
  let step = timeframe === "1W" ? 7 * DAY : DAY;
  if (timeframe === "1M" && times.length) {
    const next = new Date(times[0]); next.setUTCMonth(next.getUTCMonth() + 1);
    step = next.getTime() - times[0];
  }
  return Object.freeze({ times: Object.freeze(times), singleBarStep: step });
}

export function drawingTransform(axis: DrawingTimeAxis, context: Omit<BridgeChartDrawingMigrationContext, "dates">): DrawingTransform {
  const { viewStart, viewEnd, minimumPrice, maximumPrice } = context;
  const symbol = identity(context.symbol), timeframe = identity(context.timeframe), priceBasis = identity(context.priceBasis);
  if (!axis.times.length || !Number.isInteger(viewStart) || !Number.isInteger(viewEnd)
      || viewStart < 0 || viewEnd < viewStart || viewEnd >= axis.times.length
      || !Number.isFinite(minimumPrice) || !Number.isFinite(maximumPrice) || maximumPrice <= minimumPrice
      || !Number.isFinite(maximumPrice - minimumPrice)) {
    throw new RangeError("Drawing migration requires the original nonempty viewport and finite price scale.");
  }
  const span = viewEnd - viewStart + 1, range = maximumPrice - minimumPrice;
  const capture = (point: BridgeChartDrawingPoint): BridgeChartMarketPoint => {
    finite(point?.x, point?.y);
    const result = { time: timeAt(axis, viewStart - 0.5 + point.x * span), price: maximumPrice - point.y * range };
    finite(result.time, result.price);
    if (Math.abs(result.time) > 8.64e15) throw new RangeError("Drawing time is outside the supported UTC range.");
    return result;
  };
  return {
    capture,
    project(point) {
      finite(point?.time, point?.price);
      return { x: (indexAt(axis, point.time) - viewStart + 0.5) / span, y: (maximumPrice - point.price) / range };
    },
    anchors(start, end) {
      return { schemaVersion: "bridge.chart-market-anchors/1.0.0", symbol, timeframe, priceBasis,
        adjustmentPolicy: "fixed-price", start: capture(start), end: capture(end) };
    },
    matches(value) { return value.symbol === symbol && value.timeframe === timeframe && value.priceBasis === priceBasis; }
  };
}

export function migrationTransform(context: BridgeChartDrawingMigrationContext): DrawingTransform {
  return drawingTransform(createDrawingTimeAxis(context.dates, context.timeframe), context);
}

export function normalizeDrawingAnchors(value: BridgeChartDrawingAnchors): BridgeChartDrawingAnchors {
  if (!value || value.schemaVersion !== "bridge.chart-market-anchors/1.0.0" || value.adjustmentPolicy !== "fixed-price")
    throw new TypeError("Unsupported market drawing anchor version or adjustment policy.");
  const keys = ["schemaVersion", "symbol", "timeframe", "priceBasis", "adjustmentPolicy", "start", "end"];
  if (Object.keys(value).some(key => !keys.includes(key))) throw new TypeError("Unknown market anchor field.");
  for (const point of [value.start, value.end]) {
    if (!point || Object.keys(point).some(key => key !== "time" && key !== "price")) throw new TypeError("Invalid market point.");
    finite(point.time, point.price);
    if (Math.abs(point.time) > 8.64e15) throw new RangeError("Drawing time is outside the supported UTC range.");
  }
  return { ...value, symbol: identity(value.symbol), timeframe: identity(value.timeframe), priceBasis: identity(value.priceBasis),
    start: { ...value.start }, end: { ...value.end } };
}

export function drawingIdentity(value: string): string { return identity(value); }
function identity(value: string): string {
  if (typeof value !== "string" || !value.trim() || value !== value.trim() || value.length > 128)
    throw new TypeError("Drawing symbol, timeframe and price basis must be explicit bounded identities.");
  return value;
}
function finite(a: number, b: number): void {
  if (!Number.isFinite(a) || !Number.isFinite(b)) throw new RangeError("Drawing coordinates must be finite.");
}
function timeAt(axis: DrawingTimeAxis, index: number): number {
  const times = axis.times;
  if (times.length === 1) return times[0] + index * axis.singleBarStep;
  const left = Math.max(0, Math.min(times.length - 2, Math.floor(index)));
  return times[left] + (index - left) * (times[left + 1] - times[left]);
}
function indexAt(axis: DrawingTimeAxis, time: number): number {
  const times = axis.times;
  if (times.length === 1) return (time - times[0]) / axis.singleBarStep;
  let lo = 0, hi = times.length - 1;
  while (lo + 1 < hi) { const mid = (lo + hi) >>> 1; if (times[mid] <= time) lo = mid; else hi = mid; }
  const left = time < times[0] ? 0 : time >= times[times.length - 1] ? times.length - 2 : lo;
  return left + (time - times[left]) / (times[left + 1] - times[left]);
}
